import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { siteConfig } from "@/data/siteConfig";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.16 },
  }),
};

export const Hero = () => {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const decorY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-14 md:pt-12 md:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xs uppercase tracking-[0.3em] font-medium text-ser-purple mb-8"
          >
            {t.hero.overline}
          </motion.p>

          <h1 className="font-display text-[2.1rem] sm:text-[2.65rem] lg:text-[3.15rem] xl:text-[3.7rem] leading-[1.05] tracking-tight text-navy" data-testid="hero-headline">
            <span className="mask-line">
              <motion.span custom={0} variants={lineVariants} initial="hidden" animate="show">
                {t.hero.line1}
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span custom={1} variants={lineVariants} initial="hidden" animate="show" className="italic text-ser-purple">
                {t.hero.line2}
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="origin-left h-px w-24 bg-ser-yellow mt-10"
            aria-hidden="true"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 text-base md:text-lg leading-relaxed text-navy/70 max-w-xl"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to={`/${lang}/contact`}
              data-testid="hero-primary-cta"
              className="bg-navy text-white text-sm font-medium px-8 py-3.5 hover:bg-ser-purple transition-colors"
            >
              {t.hero.primary}
            </Link>
            <Link
              to={`/${lang}/about`}
              data-testid="hero-secondary-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-ser-purple hover:text-navy transition-colors"
            >
              {t.hero.secondary}
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block lg:pt-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: decorY }} className="absolute -top-10 -right-6 w-40 h-40 rounded-full border border-ser-blue/30" aria-hidden="true" />
            <motion.div style={{ y: decorY }} className="absolute -bottom-8 -left-10 w-24 h-24 rounded-full bg-pale-violet" aria-hidden="true" />
            <motion.img
              src={lang === "es" ? siteConfig.logoHeroEs : siteConfig.logoHeader}
              alt=""
              aria-hidden="true"
              style={{ y: imgY }}
              className="relative w-full max-w-md mx-auto animate-floaty"
              loading="eager"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
